import { ValidationError } from '~/errors';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  HydratedOwnerListPaginationDto,
  HydratedNewOwnerDto,
  CreateOwnerDto,
  PaymentAccountsDto,
  CreateOwnerPaymentAccountDto,
  PaymentAccountDto,
  PostOwnerContributionDto,
  OwnerContributionDto,
  DistributionValidatePropertiesDto,
  DistributionValidationDto,
  CalculatedPropertyDistributionsDto,
  PostOwnerDistributionsDto,
  OwnerDistributionsResultDto,
  GetOwnerTransfersParams,
  OwnerTransferPaginationDto,
  OwnerTransferDto,
  CreateOwnerTransferDto,
  UpdateOwnerDto,
  OwnerPortalInviteResponseDto,
  EstimateOwnerDistributionAchArrivalTimesDto,
  EstimateOwnerDistributionAchArrivalTimesResultDto,
  GetOwnerContributionsParams,
  OwnerContributionPaginationDto,
  GetOwnerDistributionsParams,
  OwnerDistributionPaginationDto,
  OwnerDistributionDto,
  UpdateOwnerDistributionDto,
  UpdateOwnerContributionDto,
  DisburseOwnerDistributionsDto,
  DisburseOwnerDistributionsResultDto,
  OwnerContributionRequestDto,
  RequestOwnerContributionDto,
} from '~/swagger/portal/Api';
import type { UserQueryFilter } from '~/types/apiFilters';

function validateId(ownerId: string): void {
  if (!ownerId) throw new ValidationError(`Owner ID must be provided`);
}
export type OwnersFilter = UserQueryFilter<{
  portfolioIds?: string[];
  propertyIds?: string[];
}>;

export enum OwnerTransferType {
  Contribution = 'contribution',
  Distribution = 'distribution',
  Transfer = 'transfer',
}

const BASE_URL = '/api/owners';
export class OwnersRepository extends BaseRestRepository {
  public async getOwners(filter: OwnersFilter): Promise<HydratedOwnerListPaginationDto> {
    const url = urlWithQuery(BASE_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async batchGetOwnersByIds(ownerIds: string[]): Promise<HydratedNewOwnerDto[]> {
    const url = urlWithQuery(`${BASE_URL}/batch`, { ownerIds });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getOwner(ownerId: string): Promise<HydratedNewOwnerDto> {
    validateId(ownerId);
    const url = `${BASE_URL}/${ownerId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addOwner(payload: CreateOwnerDto): Promise<HydratedNewOwnerDto> {
    const url = `${BASE_URL}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getOwnerPaymentAccounts(ownerId: string): Promise<PaymentAccountsDto> {
    const url = `${BASE_URL}/${ownerId}/payment-accounts`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async addOwnerPaymentAccount(ownerId: string, payload: CreateOwnerPaymentAccountDto): Promise<PaymentAccountDto> {
    const url = `${BASE_URL}/${ownerId}/payment-accounts`;
    const response = await this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(payload) });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerPaymentAccount(ownerId: string, paymentAccountId: string): Promise<void> {
    const url = `${BASE_URL}/${ownerId}/payment-accounts/${paymentAccountId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async addOwnerContribution(payload: PostOwnerContributionDto): Promise<OwnerContributionDto> {
    const url = `${BASE_URL}/contributions`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async addOwnerContributionRequest(payload: RequestOwnerContributionDto): Promise<OwnerContributionRequestDto> {
    const url = `${BASE_URL}/contributions/requests`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getOwnersDistributionsValidation(payload: DistributionValidatePropertiesDto): Promise<DistributionValidationDto> {
    const url = `${BASE_URL}/distributions/validate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getOwnersDistributionsCalculation(payload: DistributionValidatePropertiesDto): Promise<CalculatedPropertyDistributionsDto> {
    const url = `${BASE_URL}/distributions/calculate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async submitOwnerDistributions(payload: PostOwnerDistributionsDto): Promise<OwnerDistributionsResultDto> {
    const url = `${BASE_URL}/distributions`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async disburseOwnerDistributions(payload: DisburseOwnerDistributionsDto): Promise<DisburseOwnerDistributionsResultDto> {
    const url = `${BASE_URL}/distributions/disburse`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getOwnerTransfers(payload: GetOwnerTransfersParams): Promise<OwnerTransferPaginationDto> {
    const url = urlWithQuery(`${BASE_URL}/transfers`, payload);
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async getOwnerTransferDetails(id: string): Promise<OwnerTransferDto> {
    const url = `${BASE_URL}/transfers/${id}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerTransfers(id: string): Promise<void> {
    const url = `${BASE_URL}/transfers/${id}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async postOwnerTransfers(payload: CreateOwnerTransferDto): Promise<any> {
    const url = `${BASE_URL}/transfers`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateOwner(id: string, payload: UpdateOwnerDto): Promise<HydratedNewOwnerDto> {
    const url = `${BASE_URL}/${id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async portalInvite(ownerId: string): Promise<OwnerPortalInviteResponseDto> {
    validateId(ownerId);
    const url = `${BASE_URL}/${ownerId}/portal-invite`;
    const response = await this.fetchWithAuth(url, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async changeOwnerPassword(ownerId: string, password: string): Promise<void> {
    validateId(ownerId);
    if (!password) throw new ValidationError('Password must be provided');
    const url = `${BASE_URL}/${ownerId}/password`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      console.error(await response.json());
    }
  }

  public async deactivateOwner(ownerId: string): Promise<void> {
    validateId(ownerId);
    const url = `${BASE_URL}/${ownerId}/deactivate`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async estimateOwnerDistributionAchArrivalTimes(
    payload: EstimateOwnerDistributionAchArrivalTimesDto
  ): Promise<EstimateOwnerDistributionAchArrivalTimesResultDto> {
    const url = `${BASE_URL}/distributions/estimate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getOwnerAvailableBalance(ownerId: string, propertyId: string): Promise<any> {
    const url = `${BASE_URL}/transfers/${ownerId}/available-balance/${propertyId}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async getOwnerContributions(payload: GetOwnerContributionsParams): Promise<OwnerContributionPaginationDto> {
    const url = urlWithQuery(`${BASE_URL}/contributions`, payload);
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async getOwnerContributionRequests(payload: GetOwnerContributionsParams): Promise<any> {
    const params = {
      ...payload,
      statuses: ['Requested', 'Pending', 'Completed'],
    };
    const url = urlWithQuery(`${BASE_URL}/contributions/requests`, params);
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerContributionRequest(id: string): Promise<OwnerContributionRequestDto> {
    const url = `${BASE_URL}/contributions/requests/${id}`;
    const response = await this.fetchWithAuth(url, { method: 'DELETE' });
    return this.getJsonResponse(response);
  }

  public async getOwnerContributionDetails(id: string): Promise<OwnerContributionDto> {
    const url = `${BASE_URL}/contributions/${id}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerContribution(id: string): Promise<void> {
    const url = `${BASE_URL}/contributions/${id}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getOwnerDistributions(payload: GetOwnerDistributionsParams): Promise<OwnerDistributionPaginationDto> {
    const url = urlWithQuery(`${BASE_URL}/distributions`, payload);
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async getOwnerDistributionDetails(id: string): Promise<OwnerDistributionDto> {
    const url = `${BASE_URL}/distributions/${id}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async deleteOwnerDistribution(id: string): Promise<void> {
    const url = `${BASE_URL}/distributions/${id}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async editOwnerDistributions(ownerDistributionId: string, payload: UpdateOwnerDistributionDto): Promise<OwnerDistributionDto> {
    const url = `${BASE_URL}/distributions/${ownerDistributionId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
  public async editOwnerContribution(ownerContributionId: string, payload: UpdateOwnerContributionDto): Promise<OwnerDistributionDto> {
    const url = `${BASE_URL}/contributions/${ownerContributionId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}

export const ownerRepository = new OwnersRepository();
